// src/components/preAuth/VerifyEmailScreen.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { auth } from '../../lib/firebase.js';
import { PALETTE, THEME_CLASSES } from '../../lib/theme.js';
import { CONFIG } from '../../lib/config.js';

export default function VerifyEmailScreen({ isDark }) {
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying');
    const [errorMsg, setErrorMsg] = useState('');

    const verifyEmailState = async () => {
        try {
            setStatus('verifying');
            setErrorMsg('');

            const currentUser = auth.currentUser;
            if (!currentUser) {
                throw new Error("Identity record missing. Please re-authenticate.");
            }

            // Force query Firebase servers directly for fresh verification metadata
            await currentUser.reload();

            if (!currentUser.emailVerified) {
                throw new Error("Link not clicked yet. Please open your email and click the confirmation link.");
            }

            // Once verified, transmit ID token to authorize checkout pipeline
            const token = await currentUser.getIdToken(true);
            const res = await fetch(`${CONFIG.API_BASE_URL}/init-checkout`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to initialize payment pipelines");

            setStatus('success');
            setTimeout(() => { window.location.href = data.checkout_url; }, 1000);

        } catch (err) {
            setStatus('error');
            setErrorMsg(err.message);
        }
    };

    useEffect(() => {
        const timer = setTimeout(verifyEmailState, 2000);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className={`min-h-screen w-full flex items-center justify-center p-6 ${THEME_CLASSES.bgMain(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`} style={{ fontFamily: PALETTE.font }}>
            <div className={`w-full max-w-md p-8 rounded-[32px] backdrop-blur-2xl shadow-2xl border text-center ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)}`}>
                {status === 'verifying' && (
                    <div className="flex flex-col items-center">
                        <Loader2 className="animate-spin mb-4 text-blue-500" size={40} />
                        <h2 className="text-xl font-bold">Confirming Email Status...</h2>
                        <p className={`text-sm mt-2 ${THEME_CLASSES.textSecondary(isDark)}`}>Connecting to Firebase servers.</p>
                    </div>
                )}
                {status === 'success' && (
                    <div className="flex flex-col items-center">
                        <CheckCircle2 className="mb-4 text-green-500" size={40} />
                        <h2 className="text-xl font-bold">Email Confirmed!</h2>
                        <p className={`text-sm mt-2 ${THEME_CLASSES.textSecondary(isDark)}`}>Launching secure billing gateways...</p>
                    </div>
                )}
                {status === 'error' && (
                    <div className="flex flex-col items-center">
                        <AlertCircle className="text-red-500 mb-4" size={40} />
                        <h2 className="text-xl font-bold">Awaiting Action</h2>
                        <p className={`text-sm mt-2 mb-4 ${THEME_CLASSES.textSecondary(isDark)}`}>{errorMsg}</p>
                        <button onClick={verifyEmailState} className="px-6 py-3 text-white rounded-xl font-bold flex items-center gap-2" style={{ backgroundColor: PALETTE.blue }}>
                            <RefreshCw size={16} /> Check Verification Again
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}